import type { InventoryHealthRow } from '@api/replenishment-planning';
import type { ProductRelationEdge } from '@api/products';

/**
 * How an Inventory-health row behaves when its parent is broken down into it.
 *
 * Pure functions, kept out of the view for the same reason `grouping.ts` is: the arithmetic
 * is the part worth testing, and it is the part that is easy to get quietly wrong.
 *
 * Every field of `InventoryHealthRow` falls into exactly one of three buckets, and the split
 * is the whole design:
 *
 *   EXTENSIVE — a quantity. Scales by the edge quantity and adds. On hand, every inbound leg,
 *               DRR (units per DAY is still a quantity), the stock/in-transit splits.
 *   INTENSIVE — a ratio. Neither scales nor adds; recomputed from the extensive fields once
 *               every addition has landed. Cover days. Also reorder point and shortfall,
 *               which are not ratios but are FUNCTIONS of DRR and stock and so have to be
 *               re-derived rather than carried over.
 *   IDENTITY  — belongs to the surviving SKU, not to the parent. Supplier, tier, category,
 *               sourcing model, and the reorder rule's own terms.
 *
 * The mistake this file exists to prevent is adding an intensive field. A base SKU at 12.6
 * cover days absorbing a clearance pool at 3,800 does not land at 3,812.6 — it lands at 16.3,
 * because the truth is (391 + 114) / (30.93 + 0.03). Same numbers, and the naive answer is
 * off by two orders of magnitude.
 */

/** Deep enough that `addScaled` can never write through into the caller's data. */
export const cloneHealthRow = (row: InventoryHealthRow): InventoryHealthRow => ({
    ...row,
    inbound: { ...row.inbound },
});

/**
 * `target += quantity × source`, EXTENSIVE fields only.
 *
 * The inbound legs are added individually as well as via their two rollups. They have to be:
 * the In transit cell's tooltip reads "PO x · STN y" off the legs, so a total that moved
 * while its legs did not would put two contradictory numbers on the same row.
 */
export function addScaledHealthRow(
    target: InventoryHealthRow,
    source: InventoryHealthRow,
    quantity: number,
): void {
    target.currentStock += source.currentStock * quantity;
    // Inside currentStock, so it scales with it; null until some contributor carries it.
    if (source.stockTransfer != null) {
        target.stockTransfer = (target.stockTransfer ?? 0) + source.stockTransfer * quantity;
    }
    target.drr += source.drr * quantity;
    // The rate the THRESHOLD is sized on, and the same rate one window back. Both are units
    // per day — extensive, exactly like `drr`, and for the same reason.
    //
    // `ropDrr` has to scale independently rather than being derived from `drr`: the two are
    // read over different windows, so a parent whose demand doubled last week contributes a
    // different ratio to each. Deriving one from the other would make the exploded reorder
    // point disagree with the un-exploded one whenever a SKU's window is not the view's.
    target.ropDrr = (target.ropDrr ?? 0) + (source.ropDrr ?? 0) * quantity;
    target.drrPrevious =
        (target.drrPrevious ?? 0) + (source.drrPrevious ?? 0) * quantity;

    target.inbound.inTransitPo += source.inbound.inTransitPo * quantity;
    target.inbound.inTransitStn += source.inbound.inTransitStn * quantity;
    target.inbound.inProductionPo += source.inbound.inProductionPo * quantity;
    target.inbound.inProductionWo += source.inbound.inProductionWo * quantity;
    target.inbound.pendingPo += (source.inbound.pendingPo ?? 0) * quantity;
    target.inbound.pendingWo += (source.inbound.pendingWo ?? 0) * quantity;
    target.inbound.inTransit += source.inbound.inTransit * quantity;
    target.inbound.open += source.inbound.open * quantity;
    target.inbound.total += source.inbound.total * quantity;

    // The two disjoint splits of on-hand and of in-transit. Kept in step with their parents
    // above so the "internal + at manufacturer = on hand" identity the columns promise still
    // holds after the explosion.
    target.stockInternal = (target.stockInternal ?? 0) + (source.stockInternal ?? 0) * quantity;
    target.stockAtManufacturer =
        (target.stockAtManufacturer ?? 0) + (source.stockAtManufacturer ?? 0) * quantity;
    target.inTransitInternal =
        (target.inTransitInternal ?? 0) + (source.inTransitInternal ?? 0) * quantity;
    target.inTransitAtManufacturer =
        (target.inTransitAtManufacturer ?? 0) + (source.inTransitAtManufacturer ?? 0) * quantity;

    // Material-view columns. Both are quantities implied by the FG shortfall, so they scale.
    target.fgDeficit = (target.fgDeficit ?? 0) + (source.fgDeficit ?? 0) * quantity;
    target.rmRequired = (target.rmRequired ?? 0) + (source.rmRequired ?? 0) * quantity;
}

/**
 * Re-derive everything that is a function of the extensive fields, mirroring exactly what
 * `inventory-health.service.ts` and `replenishment-config.service.ts` do server-side. The
 * two must agree to the unit, because the un-exploded and exploded views sit one click apart
 * and any drift between them reads as a bug in the numbers rather than in the toggle.
 */
export function recomputeHealthRow(row: InventoryHealthRow): void {
    row.drr = Number(row.drr.toFixed(2));
    row.ropDrr = Number((row.ropDrr ?? 0).toFixed(2));

    // Sized on `ropDrr`, NOT `drr` — the rate at this SKU's own policy window rather than the
    // one the operator happens to be viewing. Using `drr` here would reintroduce, inside the
    // break-down, exactly the coupling the policy split removed everywhere else: the exploded
    // reorder point would slide with the DRR control while the un-exploded one held still, and
    // the two views sit one click apart.
    row.reorderPoint = Math.round(
        row.ropDrr * (row.reorderLeadTimeDays + row.reorderSafetyDays),
    );

    // INTENSIVE — a ratio, so recomputed from the two summed rates and never carried over.
    // Averaging the contributors' percentages would weight a SKU selling two units a day the
    // same as one selling two thousand. Null when the prior window has nothing to divide by,
    // matching the server's own rule.
    const previous = row.drrPrevious ?? 0;
    row.drrDeltaPct =
        previous > 0
            ? Number((((row.drr - previous) / previous) * 100).toFixed(1))
            : null;
    row.drrPrevious = previous > 0 ? Number(previous.toFixed(2)) : null;

    // Recomputed, never summed. Summing invents shortfall that does not exist: a parent can
    // be short while the SKU it breaks down into holds surplus, and after the merge that
    // surplus genuinely covers it.
    const available = row.currentStock + row.inbound.total;
    row.shortfall = Math.max(0, row.reorderPoint - available);

    // On-hand over run rate — deliberately NOT (stock + inbound) / drr, matching the server.
    row.coverDays =
        row.drr > 0 ? Number((row.currentStock / row.drr).toFixed(1)) : null;
}

/**
 * A zeroed row for a base SKU that had no row of its own — filtered out of the current
 * slice, or a virtual line like basil's `BSL-CUSTOM` that is never stocked.
 *
 * Everything extensive starts at zero so `addScaled` can accumulate into it. The grouping
 * columns come off the edge, without which the row would drop out of any category or product
 * group the grid is grouping on and its units would vanish from the totals.
 *
 * Supplier and sourcing model are genuinely unknown here — this SKU was not in the payload,
 * so nothing resolved a vendor for it. UNMAPPED is honest: it renders as "no action
 * available" rather than offering a PO to nobody.
 */
export function makeEmptyHealthRow(edge: ProductRelationEdge): InventoryHealthRow {
    return {
        productId: edge.childId,
        sku: edge.childSku,
        productName: edge.childName,
        category: edge.childCategory,
        productGroup: edge.childProductGroup,
        tier: edge.childTier,
        materialType: null,
        unit: null,
        sourcingModel: 'UNMAPPED',
        currentStock: 0,
        stockTransfer: null,
        inbound: {
            inTransitPo: 0,
            inTransitStn: 0,
            inProductionPo: 0,
            inProductionWo: 0,
            pendingPo: 0,
            pendingWo: 0,
            inTransit: 0,
            open: 0,
            total: 0,
        },
        // `recompute` re-derives this from whatever demand the row absorbs. With no rule terms
        // of its own that lands on 0, which is the right answer: we have no reorder policy for
        // a SKU nobody planned.
        reorderPoint: 0,
        reorderTier: null,
        reorderLeadTimeDays: 0,
        reorderSafetyDays: 0,
        // ORG_FALLBACK, not a made-up window. This SKU was never in the payload, so nothing
        // resolved a policy for it — claiming SKU or TIER would put a badge on the row implying
        // somebody made a decision about it.
        ropWindowDays: 0,
        ropWindowSource: 'ORG_FALLBACK',
        ropDrr: 0,
        drr: 0,
        drrPrevious: null,
        drrDeltaPct: null,
        shortfall: 0,
        coverDays: null,
        vendorId: null,
        vendorName: null,
        moq: null,
        stockInternal: 0,
        stockAtManufacturer: 0,
        inTransitInternal: 0,
        inTransitAtManufacturer: 0,
        fgDeficit: 0,
        rmRequired: 0,
    };
}

/**
 * Module-level so the identity is stable across renders — `useSkuExplosion` memoises on its
 * callbacks, and an inline arrow would rebuild the exploded set on every keystroke.
 */
export const getHealthRowId = (row: InventoryHealthRow): string => row.productId;
